<template>
  <div class="ma-3">
    <div class="title mb-3">
      Sessions
      <v-icon>mdi-chevron-right</v-icon>
      Centers management
      <v-icon>mdi-chevron-right</v-icon>
      {{ isNew ? 'New' : center.name }}
    </div>
    <div
      v-if="center.slug"
      class="legend"
    >
      <v-icon>mdi-star-four-points-small</v-icon><span>{{ center.slug }}</span>
    </div>

    <v-form
      ref="form"
      v-model="formValid"
      layout="column"
      layout-align="center center"
      @submit.prevent="save"
    >
      <div>
        <div class="title font-weight-light mb-3">
          Center information
        </div>
        <v-text-field
          v-model="center.name"
          outline
          label="Name"
          class="field-width"
          :rules="[ v => !!v || 'This field is required' ]"
        />
        <v-text-field
          v-model="center.email"
          type="email"
          outline
          label="Email"
          class="field-width"
          :rules="[ v => !!v || 'This field is required' ]"
        />
        <v-checkbox
          v-model="center.is_pmr"
          label="Accessible for people with reduced mobility"
          :true-value="1"
          :false-value="0"
        />
        <div class="title font-weight-light mb-4">
          Address
        </div>

        <address-bloc
          :key="`${center.id}-${center.updated_at}`"
          :addressable="center"
          @update="updateAddress"
        />
        <div class="title font-weight-light mb-4">
          Templates
        </div>
        <div>
          <v-text-field
            v-model="template.name"
            label="Email template"
            prepend-icon="mdi-attachment"
            @click="pickFile('template')"
          />
          <v-btn
            :disabled="!center.template_doc_id"
            flat
            outline
            color="primary"
            title="Download"
            @click="download(center.id, 'template')"
          >
            <v-icon>mdi-download</v-icon>
            Download current template
          </v-btn>
          <input
            ref="file_template"
            type="file"
            style="display: none"
            accept="text/html"
            @change="onFilePicked($event, 'template')"
          >
        </div>
        <div>
          <v-text-field
            v-model="reminder.name"
            label="Reminder template"
            prepend-icon="mdi-attachment"
            @click="pickFile('reminder')"
          />
          <v-btn
            :disabled="!center.reminder_template_id"
            flat
            outline
            color="primary"
            title="Download"
            @click="download(center.id, 'reminder')"
          >
            <v-icon>mdi-download</v-icon>
            Download current reminder
          </v-btn>
          <input
            ref="file_reminder"
            type="file"
            style="display: none"
            accept="text/html"
            @change="onFilePicked($event, 'reminder')"
          >
        </div>
        <div>
          <v-text-field
            v-model="cancellation.name"
            label="Cancellation template"
            prepend-icon="mdi-attachment"
            @click="pickFile('cancellation')"
          />
          <v-btn
            :disabled="!center.cancellation_template_id"
            flat
            outline
            color="primary"
            title="Download"
            @click="download(center.id, 'cancellation')"
          >
            <v-icon>mdi-download</v-icon>
            Download current cancellation
          </v-btn>
          <input
            ref="file_cancellation"
            type="file"
            style="display: none"
            accept="text/html"
            @change="onFilePicked($event, 'cancellation')"
          >
        </div>
      </div>

      <v-btn
        depressed
        color="primary"
        @click="save"
      >
        Save
      </v-btn>
      <router-link
        :to="{name: 'admin-sessions-centers-index'}"
        class="grey--text"
      >
        Cancel
      </router-link>
    </v-form>
  </div>
</template>

<script>
import Params from "@/api/params"
import GlobalMixin from "@/mixins/global"
import FormDataMixin from "@/mixins/formdata"
import Auth from "@/plugins/auth"
import Consts from "@/consts"
import axios from 'axios'
import AddressBloc from '@/components/shared/AddressBloc'

export default {
  components: {AddressBloc},
  mixins: [GlobalMixin, FormDataMixin],
  data() {
    return {
      center: {
        name: null,
        email: null,
        is_pmr: false,
        id: null,
        slug: "",
        country_id: null,
        number: '',
        street_id: null,
        city_id: null,
        street: '',
        zip_code: '',
        city: ''
      },
      template: {
        data: null,
        name: ""
      },
      reminder: {
        data: null,
        name: ""
      },
      cancellation: {
        data: null,
        name: ""
      },
      formValid: false,
      Consts,
      Auth
    };
  },
  computed: {
    isNew() {
      return this.$route.params.id === "new";
    }
  },
  mounted() {
    if (!this.isNew) {
      this.fetch();
    }
  },
  methods: {
    fetch() {
      Params.get("centers", this.$route.params.id).then(res => {
        this.center = res.data;
      });
    },
    save() {
      if (!this.$refs.form.validate()) {
        return;
      }
      let formData = new FormData();
      if (this.template.data && this.template.name) {
          formData.append("template_doc_id", this.template.data, this.template.name);
      }
      if (this.reminder.data && this.reminder.name) {
          formData.append("reminder_template_id", this.reminder.data, this.reminder.name);
      }
      if (this.cancellation.data && this.cancellation.name) {
          formData.append("cancellation_template_id", this.cancellation.data, this.cancellation.name);
      }

      this._convertObjectToFormData(formData, this.center);

      if (this.isNew) {
        Params.create("centers", formData, true).then(() => {
          this.$router.push({ name: "admin-sessions-centers-index" });
          this.$snotify.success(
            `The center "${this.center.name}" has been created`
          );
        });
      } else {
        Params.update("centers", this.center.id, formData, true).then(() => {
          this.$router.push({ name: "admin-sessions-centers-index" });
          this.$snotify.success(
            `The center "${this.center.name}" has been updated`
          );
        });
      }
    },
    pickFile(type) {
      const ref = 'file_' + type
      this.$refs[ref].click()
    },
    onFilePicked(e, type) {
        const files = e.target.files;
        if (files[0] !== undefined) {
          const ref = this[type]
          ref.data = files[0];
          ref.name = files[0].name;
        }
    },
    download (id, type) {
        let a = document.createElement("a");
        document.body.appendChild(a);
        a.style = "display: none";

        axios.get(`/params/centers/${id}/${type}`, {responseType: 'blob'}).then(res => {
            let blob = new Blob([res.data], {type: "octet/stream"});

            if (window.navigator && window.navigator.msSaveOrOpenBlob){
                window.navigator.msSaveBlob(blob, `${type}_${id}.html`);
            }
            else
            {
                let url = window.URL.createObjectURL(blob);
                a.href = url;
                a.download = `${type}_${id}.html`;
                a.click();
                window.URL.revokeObjectURL(url);
            }
        });
    },
    updateAddress(address) {
      this.center.country_id = address.country_id
      this.center.number = address.number
      this.center.street_id = address.street_id
      this.center.city_id = address.city_id
      this.center.street = address.street
      this.center.zip_code = address.zip_code
      this.center.city = address.city
    }
  }
};
</script>

<style scoped lang="sass">
  .legend
    color: #A1A1A1
    display: flex
    flex-direction: row
    align-items: center
</style>
